/* eslint-disable @typescript-eslint/unbound-method */
import { el } from "@dhx/ts-common/dom";
import { IDataItem } from "@dhx/ts-data";
import { IEventSystem } from "@dhx/ts-common/events";
import {
	DataEvents,
	Diagram,
	DiagramEvents,
	DiagramType,
	IBaseCoords,
	ItemConfig,
	ILineConfig,
	SelectionEvents,
} from "@dhx/ts-diagram";
import { DiagramEditorEvents, ICoords } from "../types";
import { drag } from "../helpers/drag";
import { BlockSelection } from "../BlockSelection";
import { Connect } from "./connect";
import { Resize } from "./resize";
import { Id } from "@dhx/ts-common/types";
import {
	getDistanceEblowLine,
	getDistancePercentage,
	getLinePercentage,
	getNearPointStraightLine,
} from "@dhx/ts-diagram/sources/helpers/lineHelpers";

interface ICoordsUdate {
	x: number;
	y: number;
	width: number;
	height: number;
	dx?: number;
	dy?: number;
}

function startDrag(e: MouseEvent, n) {
	drag.start(e, n.data.resizer, n.data.p);
}

export function getLength(from: ItemConfig, to: ItemConfig): number {
	const x = to.x - from.x;
	const y = to.y - from.y;
	return Math.sqrt(x * x + y * y);
}

function getMoveUpdateShapeConfig(item: ItemConfig, move: ICoords, mode: DiagramType = "default") {
	if (mode === "default") {
		return {
			x: (item.x || 0) + move.x,
			y: (item.y || 0) + move.y,
		};
	}
	return {
		dx: (item.dx || 0) + move.x,
		dy: (item.dy || 0) + move.y,
	};
}

function changeMoveUpdateShapeConfig(item: ItemConfig, move: ICoords, mode: DiagramType = "default") {
	if (mode === "default") {
		item.x = (item.x || 0) + move.x;
		item.y = (item.y || 0) + move.y;
		return;
	}
	item.dx = (item.dx || 0) + move.x;
	item.dy = (item.dy || 0) + move.y;
}

export class Controls {
	public connect: Connect;

	private _events: IEventSystem<DataEvents | SelectionEvents | DiagramEvents | DiagramEditorEvents>;
	private _diagram: Diagram;
	private _blockSelection: BlockSelection;
	private _resize: Resize;
	private _diagramSize: any;
	private _isOrgType: boolean;

	constructor(
		events: IEventSystem<DataEvents | SelectionEvents | DiagramEvents | DiagramEditorEvents>,
		diagram: Diagram,
		blockSelection: BlockSelection
	) {
		this._events = events;
		this._diagram = diagram;
		this._isOrgType = ["org", "mindmap"].includes(this._diagram.config.type);
		this.connect = new Connect(this, this._diagram);
		this._blockSelection = blockSelection;
		this._resize = new Resize(this, this._diagram);

		this._diagram.events.on(
			DiagramEvents.lineTitleMouseDown,
			(lineId: Id, titleId: Id, event: MouseEvent) => {
				const line = this._diagram.data.getItem(lineId);
				const lineText = line.title.text.find(({ id }) => id === titleId);

				if (lineText.$editable || lineText.$item.isFixed() || !lineText.draggable) {
					return;
				}

				drag.start(
					event,
					{
						onMove: (event: MouseEvent) => {
							if (
								!this._events.fire(DiagramEditorEvents.beforeLineTitleMove, [
									event,
									lineId,
									titleId,
									{ x: lineText.$x, y: lineText.$y },
								])
							) {
								return;
							}

							const cp = diagram.getCurrentCoords(event);
							const sp = line.points[0];
							const ep = line.points[line.points.length - 1];

							const baseStartPoint = { ...sp };
							const baseEndPoint = { ...ep };

							if (line.points.length > 2) {
								for (let index = 0; index < line.points.length; index++) {
									const { x: X, y: Y } = line.points[index];

									if (baseStartPoint.x > X) {
										baseStartPoint.x = X;
									}
									if (baseEndPoint.x < X) {
										baseEndPoint.x = X;
									}
									if (baseStartPoint.y > Y) {
										baseStartPoint.y = Y;
									}
									if (baseEndPoint.y < Y) {
										baseEndPoint.y = Y;
									}
								}
							}

							if (baseStartPoint.x < baseEndPoint.x) {
								if (baseStartPoint.x >= cp.x) {
									cp.x = baseStartPoint.x;
								}
								if (baseEndPoint.x <= cp.x) {
									cp.x = baseEndPoint.x;
								}
							}
							if (baseStartPoint.y < baseEndPoint.y) {
								if (baseStartPoint.y >= cp.y) {
									cp.y = baseStartPoint.y;
								}
								if (baseEndPoint.y <= cp.y) {
									cp.y = baseEndPoint.y;
								}
							}
							if (baseStartPoint.x > baseEndPoint.x) {
								if (baseEndPoint.x >= cp.x) {
									cp.x = baseEndPoint.x;
								}
								if (baseStartPoint.x <= cp.x) {
									cp.x = baseStartPoint.x;
								}
							}
							if (baseStartPoint.y > baseEndPoint.y) {
								if (baseEndPoint.y >= cp.y) {
									cp.y = baseEndPoint.y;
								}
								if (baseStartPoint.y <= cp.y) {
									cp.y = baseStartPoint.y;
								}
							}

							if (line.connectType === "straight" || line.points.length === 2) {
								const linePoint = getNearPointStraightLine(sp, ep, cp);
								const percentage = getLinePercentage(sp, ep, linePoint);
								const distance = getDistancePercentage(percentage);

								lineText.distance = distance;
							} else {
								lineText.distance = getDistanceEblowLine(line, cp);
							}

							diagram.toolbar.hide();
							diagram.data.update(lineId, line);

							this._events.fire(DiagramEditorEvents.afterLineTitleMove, [
								event,
								lineId,
								titleId,
								{ x: lineText.$x, y: lineText.$y },
							]);
						},
						onUp: (event: MouseEvent) => {
							this._events.fire(DiagramEditorEvents.lineTitleMoveEnd, [
								event,
								lineId,
								titleId,
								{ x: lineText.$x, y: lineText.$y },
							]);

							diagram._lastItemClickCoords = diagram.getCurrentCoords(event);
							diagram.toolbar.show();
							diagram.paint();
						},
					},
					null
				);
				this._diagram.events.on(DiagramEvents.beforeRender, size => (this._diagramSize = size));
			}
		);

		this._diagram.events.on(DiagramEvents.groupMouseDown, (id: Id, event: MouseEvent) => {
			this.connect.removeNearShape();

			const gridStep = this._diagram.config.gridStep;
			let group = this._diagram.data.getItem(id);

			if (!group || group.$editable) {
				return;
			}
			if (group.type === "$sgroup") {
				group = this._diagram.data.getItem(group.$group);
			}
			if (!group.$blockSelected && !event.shiftKey && !event.altKey) {
				this._blockSelection.clearSelection();
			}

			drag.start(
				event,
				{
					onMove: (event: MouseEvent, mov: ICoords, _p: any) => {
						if (!event.shiftKey && !event.altKey) {
							this._diagram.selection.add(group.id);
						}

						mov.x = Math.round(mov.x / gridStep) * gridStep;
						mov.y = Math.round(mov.y / gridStep) * gridStep;

						if (
							!this._events.fire(DiagramEditorEvents.beforeGroupMove, [
								event,
								id,
								{ x: group.x, y: group.y },
							]) ||
							!this._events.fire(DiagramEditorEvents.beforeItemMove, [
								event,
								id,
								{ x: group.x, y: group.y },
								mov,
							])
						) {
							return;
						}

						const items = !group.$blockSelected
							? [group]
							: this._blockSelection
									.getSelected()
									.map(selectedId => diagram.data.getItem(selectedId));

						items.forEach((item: IDataItem) => {
							if (item.id === group.id) {
								return;
							}
							const type = item.$item.getBaseType();
							changeMoveUpdateShapeConfig(item, mov);
							if (type === "group" || type === "swimlane") {
								diagram.data.eachChild(item.id, i => {
									changeMoveUpdateShapeConfig(i, mov);
								});
							}
						});
						diagram.data.eachChild(group.id, item => {
							changeMoveUpdateShapeConfig(item, mov);
						});
						diagram.data.update(group.id, getMoveUpdateShapeConfig(group, mov));

						diagram.toolbar.hide();

						this._events.fire(DiagramEditorEvents.afterGroupMove, [
							event,
							id,
							{ x: group.x, y: group.y },
						]);
						this._events.fire(DiagramEditorEvents.afterItemMove, [
							event,
							id,
							{ x: group.x, y: group.y },
						]);
					},
					onUp: () => {
						this._events.fire(DiagramEditorEvents.groupMoveEnd, [
							event,
							id,
							{ x: group.x, y: group.y },
						]);
						this._events.fire(DiagramEditorEvents.itemMoveEnd, [
							event,
							id,
							{ x: group.x, y: group.y },
						]);
						diagram.toolbar.show();
						diagram.paint();
					},
				},
				null
			);
			this._diagram.events.on(DiagramEvents.beforeRender, size => (this._diagramSize = size));
		});

		this._diagram.events.on(DiagramEvents.shapeMouseDown, (id: Id, event: MouseEvent) => {
			this.connect.removeNearShape();
			const gridStep = this._diagram.config.gridStep;
			const shape = this._diagram.data.getItem(id) as ItemConfig;

			if (!shape || shape.$editable) {
				return;
			}
			if (!event.shiftKey && !event.altKey) {
				this._diagram.selection.add(id);
			}
			if (shape.$connectMode) {
				this.connect.setNearShape(shape);
			}
			if (!shape.$blockSelected && !event.shiftKey && !event.altKey) {
				this._blockSelection.clearSelection();
			}

			drag.start(
				event,
				{
					onMove: (event: Event, mov: ICoords, _p: any) => {
						mov.x = Math.round(mov.x / gridStep) * gridStep;
						mov.y = Math.round(mov.y / gridStep) * gridStep;

						if (
							!this._events.fire(DiagramEditorEvents.beforeShapeMove, [
								event,
								id,
								{ x: shape.x, y: shape.y },
							]) ||
							!this._events.fire(DiagramEditorEvents.beforeItemMove, [
								event,
								id,
								{ x: shape.x, y: shape.y },
								mov,
							])
						) {
							return;
						}

						const items = !shape.$blockSelected
							? [shape]
							: this._blockSelection
									.getSelected()
									.map(selectedId => diagram.data.getItem(selectedId));

						items.forEach((item: IDataItem) => {
							if (item.id === shape.id) return;
							const type = item.$item.getBaseType();
							if (type === "group" || type === "swimlane") {
								changeMoveUpdateShapeConfig(item, mov);
								diagram.data.eachChild(item.id, i => {
									changeMoveUpdateShapeConfig(i, mov);
								});
							} else {
								changeMoveUpdateShapeConfig(item, mov, diagram.config.type);
							}
						});
						diagram.data.update(
							shape.id,
							getMoveUpdateShapeConfig(shape, mov, diagram.config.type)
						);

						if (shape.$item.getBaseType() === "shape") {
							diagram.data
								.findAll(
									(i: ItemConfig) =>
										i.$item.getBaseType() === "line" &&
										(i.from === shape.id || i.to === shape.id)
								)
								.forEach((c: ILineConfig) => {
									const connector = c as ILineConfig;
									connector.customGap = undefined;
								});
						}

						diagram.toolbar.hide();
						diagram.paint();

						this._events.fire(DiagramEditorEvents.afterShapeMove, [
							event,
							id,
							{ x: shape.x, y: shape.y },
						]);
						this._events.fire(DiagramEditorEvents.afterItemMove, [
							event,
							id,
							{ x: shape.x, y: shape.y },
						]);
					},
					onUp: () => {
						this._events.fire(DiagramEditorEvents.shapeMove, []); // Deprecated from 4.0
						this._events.fire(DiagramEditorEvents.shapeMoveEnd, [
							event,
							id,
							{ x: shape.x, y: shape.y },
						]);
						this._events.fire(DiagramEditorEvents.itemMoveEnd, [
							event,
							id,
							{ x: shape.x, y: shape.y },
						]);
						diagram.toolbar.show();
						diagram.paint();
					},
				},
				null
			);
			this._diagram.events.on(DiagramEvents.beforeRender, size => (this._diagramSize = size));
		});

		this._diagram.events.on(SelectionEvents.afterUnSelect, id => {
			const item = this._diagram.data.getItem(id);
			if (item && item.$item.getBaseType() === "line" && item.$selectedPoint) {
				item.$selectedPoint = null;
			}
		});
	}
	render(item: ItemConfig, size: any) {
		if (!item) {
			const connect = [this.connect.getPoints(item, size)];
			return el(
				".dhx_controls",
				{
					style: {
						zIndex: 0,
					},
					onmousedown: {
						".dhx_connect_grip": startDrag,
						".dhx_connect_point": startDrag,
					},
				},
				connect
			);
		}
		if (item.$item.canResize() === false) {
			return;
		}
		let items;
		if (this._isOrgType) {
			items = [this._resize.getPoints(item, size)];
		} else {
			items = [
				this.connect.getPoints(item, size),
				this.connect.getItemId() != item.id && this._resize.getPoints(item, size),
			];
		}
		return el(
			".dhx_controls",
			{
				style: {
					zIndex: 0,
				},
				onmousedown: {
					".dhx_resize_grip": startDrag,
					".dhx_connect_grip": startDrag,
					".dhx_connect_point": startDrag,
					".dhx_shape_rotate": startDrag,
				},
				onclick: {
					".dhx_resize_grip": this._gripClick,
				},
			},
			items
		);
	}
	setNearShape(shape: ItemConfig) {
		this.connect.setNearShape(shape);
	}
	toggleNearShape(shape: ItemConfig) {
		this.connect.toggleNearShape(shape);
	}
	getPoint(x: number, y: number): ICoords {
		const diagram = this._diagram.getRootView().node.el;
		const diagramRect = diagram.getBoundingClientRect();
		const diagramMargins = this._diagram.config.margin;
		const size = this._diagramSize || { left: -diagramMargins.x, top: -diagramMargins.y };
		const scale = this._diagram.config.scale;
		const scrolls = {
			x: diagram.scrollLeft,
			y: diagram.scrollTop,
		};
		return {
			x: (x - diagramRect.left + scrolls.x) / scale - diagramMargins.x + (size.left + diagramMargins.x),
			y: (y - diagramRect.top + scrolls.y) / scale - diagramMargins.y + (size.top + diagramMargins.y),
		};
	}
	onMove(event: MouseEvent, mov: ICoords, p): void {
		const item = this._diagram.selection.getItem();
		if (!item || item.$item.isFixed()) {
			return;
		}
		if (p.rotate) {
			this._rotate(event, item);
		}
		if (p.$break) {
			p.$break = false;
			const point = { x: p.dx, y: p.dy, $custom: true };
			item.points.splice(p.i, 0, point);
		}

		const { id, x, y, height, width, $item, dy } = item;
		const update: ICoordsUdate = {
			x,
			y,
			width,
			height,
		};
		const gridStep = this._diagram.config.gridStep;

		mov.x = Math.round(mov.x / gridStep) * gridStep;
		mov.y = Math.round(mov.y / gridStep) * gridStep;

		const type = $item.getBaseType();

		if (type === "line") {
			this._diagram.toolbar.hide();
			this.connect.moveConnector(event, item, mov, p);
			return;
		}

		const isRightSide = p.dx === -1;
		const isLeftSide = p.dx === 1;
		const isTopSide = p.dy === 1;
		const isBottomSide = p.dy === -1;

		if (type === "group") {
			const { right, left, top, bottom } = item.$item.getChildBox();
			const withoutChilds = right + left + top + bottom === 0;
			const minValue = item?.header?.height || 40;

			if (withoutChilds) {
				if (isRightSide && width + mov.x >= minValue) {
					update.width += mov.x;
				}
				if (isLeftSide && width - mov.x >= minValue) {
					update.width += -mov.x;
					update.x += mov.x;
				}
				if (isBottomSide && height + mov.y >= minValue) {
					update.height += mov.y;
				}
				if (isTopSide && height - mov.y >= minValue) {
					update.height += -mov.y;
					update.y += mov.y;
				}
			} else {
				if (isRightSide && x + width + mov.x >= right) {
					update.width += mov.x;
				}
				if (isLeftSide && x + mov.x <= left) {
					update.width += -mov.x;
					update.x += mov.x;
				}
				if (isBottomSide && y + height + mov.y >= bottom) {
					update.height += mov.y;
				}
				if (isTopSide && y + mov.y <= top) {
					update.height += -mov.y;
					update.y += mov.y;
				}
			}

			$item.setMinBox();
		}
		if (type === "swimlane") {
			let right = 0,
				left = 0,
				top = 0,
				bottom = 0;

			this._diagram.data.eachChild(
				item.id,
				child => {
					const {
						left: childLeft,
						right: childRight,
						top: childTop,
						bottom: childBottom,
					} = child.$item.getChildBox();

					if (childLeft > left) left = childLeft;
					if (childRight > right) right = childRight;
					if (childTop > top) top = childTop;
					if (childBottom > bottom) bottom = childBottom;

					child.$item.setMinBox();
				},
				false
			);

			const withoutChilds = right + left + top + bottom === 0;
			const offset = isRightSide || isLeftSide ? item.layout[0].length : item.layout.length;
			const minValue = (item?.header?.height || 40) * offset;

			if (withoutChilds) {
				if (isRightSide && width + mov.x >= minValue) {
					update.width += mov.x;
				}
				if (isLeftSide && width - mov.x >= minValue) {
					update.width += -mov.x;
					update.x += mov.x;
				}
				if (isBottomSide && height + mov.y >= minValue) {
					update.height += mov.y;
				}
				if (isTopSide && height - mov.y >= minValue) {
					update.height += -mov.y;
					update.y += mov.y;
				}
			} else {
				if (isRightSide && x + width + mov.x >= right) {
					update.width += mov.x;
				}
				if (isLeftSide && x + mov.x <= left) {
					update.width += -mov.x;
					update.x += mov.x;
				}
				if (isBottomSide && y + height + mov.y >= bottom) {
					update.height += mov.y;
				}
				if (isTopSide && y + mov.y <= top) {
					update.height += -mov.y;
					update.y += mov.y;
				}
			}

			$item.setMinBox();
		}
		if (type === "shape") {
			const minSize = 30;
			if (p.dx === 1) {
				update.x += mov.x;
				update.width = width - mov.x;
			} else {
				update.width = item.width - mov.x * p.dx;
			}
			if (update.width >= minSize) {
				// update.dx = (item.dx || 0) + mov.x * p.dx;
			} else {
				update.width = width;
			}

			update.height = height - mov.y * p.dy;

			if (update.height >= minSize) {
				if (this._isOrgType) {
					update.dy = (dy || 0) + mov.y * (p.dy === 1 ? 1 : 0);
				} else {
					update.y = (y || 0) + mov.y * (p.dy === 1 ? 1 : 0);
				}
			} else {
				update.height = height;
			}
		}
		if (item.$group && !this._moveChilds(id, mov)) {
			return;
		}
		this._diagram.data.update(id, update);
	}
	onUp(event?: MouseEvent) {
		this.connectOnUp(event);
		this._events.fire(DiagramEditorEvents.shapeResize, []);
	}
	connectOnUp(event: MouseEvent) {
		if (!event) return;

		this.connect.onUp();
		this._diagram._lastItemClickCoords = this._diagram.getCurrentCoords(event);
		this._diagram.toolbar.show();
		this._diagram.paint();
	}
	protected _moveChilds(id: Id, mov: IBaseCoords): boolean {
		const item = this._diagram.data.getItem(id);
		const selectItem = this._diagram.selection.getItem();

		if (item.$item.isFixed()) {
			return false;
		}
		if (item.$group) {
			const group = this._diagram.data.getItem(item.$group);
			if (group.$group) {
				this._moveChilds(group.id, mov);
			}
			return group.$item.trackChildMove(selectItem.$item.getBox(), {
				x: mov.x,
				y: mov.y,
			});
		}
	}
	private _rotate(event: MouseEvent, item: ItemConfig): void {
		const { x, y } = this.getPoint(event.screenX, event.screenY);
		const center = {
			x: item.x + item.width / 2,
			y: item.y + item.height / 2,
		};
		let radians = Math.atan2(y - center.y, x - center.x);

		if (radians < 0) {
			radians += 2 * Math.PI;
		}

		const angle = Math.round(radians * (180 / Math.PI));
		this._diagram.data.update(item.id, { angle });
	}
	private _gripClick(_ev, node) {
		const item = node.data.resizer._diagram.selection.getItem();
		const point = node.data.p;
		if (item.$item.getBaseType() === "line" && !point.$break) {
			item.$selectedPoint = point.i;
		}
	}
}
